import { KpiStrip, MetricCard, Panel } from '@/components/erp/primitives'
import { BucketAxis, ChartFrame, RankedBars, Sparkbars } from '@/shared/ui/charts'
import { Note } from '@/shared/ui/states'
import { fmtDate, fmtNumber } from '@/shared/lib/format'
import { analyticsOverview } from '../api'
import {
  MixedCurrencyNote, Money, Pct, UnattributedNote, Unmatchable, Value,
} from '../parts'
import { CATEGORY_ORDER, PROGRAM_STATUS_ORDER, categoryLabel, programStatusLabel } from '../vocab'
import { AnalyticsScreen, useAnalyticsFilters } from './shell'

/**
 * The portfolio, in the two numbers PollSphere could only produce as one.
 *
 * `beneficiaryTotal` is the SUM of the programme rows. `distinctSubjects` is
 * how many PEOPLE that is. On a portfolio where the same 4,000 households are
 * in a bursary, a water project and a boda scheme, the sum is three times the
 * headcount, and a press release quoting the sum is not wrong so much as it is
 * answering a question nobody asked. Both are on the strip, adjacent, labelled.
 *
 * The de-duplication only works for people with a national ID recorded, so
 * `unmatchableSubjects` rides underneath. "0 duplicates" over a cohort half of
 * whom cannot be compared is a lie told with a true number.
 */
export function ProgramOverviewScreen() {
  /**
   * Why the filter state is read HERE and not inside the render callback.
   *
   * "No programmes match this filter" is the right sentence exactly once: when
   * a filter is set. With no filter set it blames a control the reader never
   * touched, and sends them looking for a chip to clear that is not there. The
   * real cause in that case is upstream — the rollup has no row for today — and
   * the notice above the panels already says so, so this copy only has to stop
   * contradicting it.
   */
  const f = useAnalyticsFilters()
  const filtered = f.status.length > 0 || f.category.length > 0
  const noProgrammes = filtered
    ? 'No programmes match this filter.'
    : 'Nothing in the portfolio snapshot yet.'

  return (
    <AnalyticsScreen
      title="Portfolio overview"
      description="Everything in scope, de-duplicated where the data allows it."
      load={(f) => analyticsOverview(f)}
      isEmpty={(d) => d.programmes === 0}
      exportKind="PROGRAMMES"
    >
      {(d) => {
        const trend = d.trend ?? []
        const enrolled = trend.map((p) => ({
          label: fmtDate(p.date),
          value: p.enrolled,
          hint: `${fmtNumber(p.enrolled)} enrolled · ${fmtNumber(p.enrolledCumulative)} to date`,
        }))
        const delivered = trend.map((p) => ({
          label: fmtDate(p.date),
          value: p.delivered,
          hint: `${fmtNumber(p.delivered)} deliveries`,
        }))
        const dropped = trend.map((p) => ({
          label: fmtDate(p.date),
          value: p.dropped,
          hint: `${fmtNumber(p.dropped)} dropped`,
        }))

        const categories = CATEGORY_ORDER
          .map((c) => ({ label: categoryLabel(c), value: d.byCategory?.[c] ?? 0 }))
          .filter((p) => p.value > 0)
          .sort((a, b) => b.value - a.value)

        const statuses = PROGRAM_STATUS_ORDER
          .map((s) => ({ label: programStatusLabel(s), value: d.byStatus?.[s] ?? 0 }))
          .filter((p) => p.value > 0)

        return (
          <div className="space-y-4">
            <KpiStrip>
              <MetricCard
                label="Programmes"
                value={fmtNumber(d.programmes)}
                sub={`${fmtNumber(d.byStatus?.ACTIVE ?? 0)} active`}
              />
              <MetricCard
                label="People reached"
                value={fmtNumber(d.distinctSubjects)}
                accent
                sub={(
                  <span title="Distinct people, matched on national ID. The sum counts a person once per programme.">
                    {fmtNumber(d.beneficiaryTotal)} enrolments across all programmes
                  </span>
                )}
              />
              <MetricCard
                label="Coverage of eligible"
                value={<Pct value={d.coverage} reason={d.coverageUnavailableReason} shortReason />}
                sub={d.eligibleTotal != null
                  ? `${fmtNumber(d.enrolledLinkedTotal)} of ${fmtNumber(d.eligibleTotal)} eligible`
                  : 'No eligible pool computed for this filter'}
              />
              <MetricCard
                label="Spend"
                value={<Money value={d.spendTotal} currency={d.currency} mixedReason={d.currencyMixedReason} />}
                // A null under a mixed selection is a REFUSAL, not an absence.
                // "No budget set" would be a false statement about the data —
                // the budgets exist, they are in two currencies and were not
                // added together.
                sub={d.currencyMixedReason
                  ? 'Withheld — the selection spans more than one currency'
                  : d.budgetTotal != null
                    ? <>of <Money value={d.budgetTotal} currency={d.currency} /> budgeted</>
                    : 'No budget set on the programmes in scope'}
              />
              <MetricCard
                label="Cost per person"
                value={<Money value={d.netCostPerUniquePerson} currency={d.currency} mixedReason={d.currencyMixedReason} />}
                sub={d.currencyMixedReason
                  ? 'Withheld — the selection spans more than one currency'
                  : (
                    <span title="Attributed spend ÷ distinct people. The per-enrolment figure divides the same spend by a larger number and is always smaller.">
                      <Money value={d.costPerBeneficiary} currency={d.currency} /> per enrolment
                    </span>
                  )}
              />
            </KpiStrip>

            <MixedCurrencyNote reason={d.currencyMixedReason} what="Every money figure on this screen" />
            <Unmatchable count={d.unmatchableSubjects} />
            <UnattributedNote amount={d.spendUnattributed} currency={d.currency} mixedReason={d.currencyMixedReason} />

            <Panel title="Activity" description="Each panel scales to its own peak — the heights are not comparable across panels.">
              <div className="grid gap-5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
                <ChartFrame title="Enrolments" description="new beneficiaries per day">
                  <Sparkbars points={enrolled} />
                  <BucketAxis points={enrolled} />
                </ChartFrame>
                <ChartFrame title="Deliveries" description="delivery events per day">
                  <Sparkbars points={delivered} />
                  <BucketAxis points={delivered} />
                </ChartFrame>
                <ChartFrame title="Drop-outs" description="beneficiaries marked dropped">
                  <Sparkbars points={dropped} emptyMessage="Nobody dropped in this window." />
                  <BucketAxis points={dropped} />
                </ChartFrame>
              </div>
            </Panel>

            <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
              <Panel title="By category" description="Programme counts. A programme has exactly one category.">
                <RankedBars points={categories} emptyMessage={noProgrammes} />
              </Panel>
              <Panel title="By status">
                <RankedBars points={statuses} emptyMessage={noProgrammes} />
                <div className="mt-3"><Note tone="info">
                  A draft programme has a budget and no beneficiaries. It is counted here and
                  contributes nothing to the reach and coverage figures above.
                </Note></div>
              </Panel>
            </div>

            <p className="text-erp-xs text-muted-foreground">
              Enrolments to date: <Value value={d.enrolledLinkedTotal} /> linked to a voter record.
              Coverage can only be computed for those — an enrolment with no linked record has no
              ward and so cannot be placed against an eligible pool.
            </p>
          </div>
        )
      }}
    </AnalyticsScreen>
  )
}
